import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Circle } from "@phosphor-icons/react";

export function FraudChecklist({ missing }) {
  if (!missing) return null;
  const items = [
    ...(missing.mandatory_present || []).map(d => ({ d, state: "present", mandatory: true })),
    ...(missing.mandatory_missing || []).map(d => ({ d, state: "missing", mandatory: true })),
    ...(missing.recommended_missing || []).map(d => ({ d, state: "missing", mandatory: false })),
  ];
  return (
    <div className="border border-gray-200 rounded-md p-4" data-testid="fraud-checklist">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-900 capitalize">{(missing.claim_type || "").replace(/_/g, " ")}</p>
        <Badge variant="outline" className={`text-xs ${missing.completeness_pct === 100 ? "bg-green-50 text-green-700 border-green-200" : "bg-yellow-50 text-yellow-800 border-yellow-300"}`} data-testid="checklist-completeness">
          {missing.completeness_pct}% complete
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map(({ d, state, mandatory }) => (
          <div key={d} className="flex items-center gap-2 text-xs" data-testid={`checklist-${d}-${state}`}>
            {state === "present" ? <CheckCircle size={14} weight="fill" className="text-green-600" />
              : mandatory ? <XCircle size={14} weight="fill" className="text-red-600" />
              : <Circle size={14} className="text-gray-300" />}
            <span className={`capitalize ${state === "missing" && mandatory ? "text-red-800 font-semibold" : "text-gray-700"}`}>{d.replace(/_/g, " ")}</span>
            {!mandatory && <span className="text-[10px] text-gray-400">optional</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function confColor(c) {
  return c >= 0.85 ? "bg-green-500" : c >= 0.6 ? "bg-yellow-500" : "bg-red-500";
}

function renderValue(v) {
  if (Array.isArray(v)) return `${v.length} item(s)`;
  if (v && typeof v === "object") return Object.entries(v).filter(([, x]) => x !== "" && x != null).map(([k, x]) => `${k.replace(/_/g, " ")}: ${x}`).join(" · ") || "—";
  return String(v);
}

export function FraudFieldsPanel({ fieldsDocs, selectedPage }) {
  const doc = (fieldsDocs || []).find(f => f.page_number === selectedPage);
  if (!doc || !Object.keys(doc.fields || {}).length) {
    return <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-md p-4 text-center" data-testid="fraud-fields-empty">No structured fields for page {selectedPage}. Select a bill, discharge summary or ID page.</p>;
  }
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden" data-testid="fraud-fields-panel">
      <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
        <p className="label-xs">Page {doc.page_number} · {(doc.classification || "").replace(/_/g, " ")}</p>
        {doc.low_confidence_fields?.length > 0 && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">{doc.low_confidence_fields.length} low confidence</Badge>}
      </div>
      <div className="divide-y divide-gray-100 max-h-72 overflow-auto">
        {Object.entries(doc.fields).map(([k, f]) => (
          <div key={k} className="px-3 py-1.5 flex items-start gap-3 text-xs" data-testid={`field-${k}`}>
            <span className="w-32 flex-shrink-0 text-gray-500 capitalize">{k.replace(/_/g, " ")}</span>
            <span className="flex-1 text-gray-900 break-words">{renderValue(f.value)}</span>
            <span className="flex items-center gap-1.5 flex-shrink-0 w-20 justify-end">
              <span className="w-10 h-1.5 bg-gray-200 rounded overflow-hidden"><span className={`block h-full ${confColor(f.confidence)}`} style={{ width: `${f.confidence * 100}%` }} /></span>
              <span className="font-mono text-gray-600 w-7 text-right">{Math.round(f.confidence * 100)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
